import { Spinner } from '@karnov/kui-react'
import { useCallback, useContext, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { useUpdateConversationMutation } from 'api/conversations/mutations'
import {
  updateConversationSubjectByIdQueryClient,
  updateConversationIdPinnedQueryClient,
} from 'api/conversations/setters'
import { PendingConversationIdsContext } from 'contexts/pendingConversationsProvider'
import { useDebouncedSearchValue } from 'hooks/useDebouncedSearchValue'
import { Conversation } from 'types/Api'

import { cn } from 'utils/cn'
import { isMobile } from 'utils/isMobile'
import { triggerToastNotification } from 'utils/triggerToastNotification'

import { ActionButtons } from './ActionButtons'
import { ConversationSubject } from './ConversationSubject'
import { Input } from './Input'

import type { FC, MouseEvent, KeyboardEvent } from 'react'

import type { TriggerEvent } from 'api/events/types'

type Props = {
  activeConversationId: string | undefined
  conversation: Conversation
  isPinned?: boolean
  onClickDeleteConversation: (conversation: Conversation) => void
  onSelectConversation: (conversationId: string) => void
  onTriggerEvent: TriggerEvent
}

export const ConversationItem: FC<Props> = ({
  activeConversationId,
  conversation,
  isPinned,
  onClickDeleteConversation,
  onSelectConversation,
  onTriggerEvent,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'conversation_history',
  })

  const inputRef = useRef<HTMLInputElement>(null)

  const [isEditMode, setIsEditMode] = useState(false)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  const debouncedValue = useDebouncedSearchValue()

  const {
    mutate: updateConversationSubject,
    isPending: isConversationSubjectUpdating,
  } = useUpdateConversationMutation({
    onError: () => {
      triggerToastNotification({
        duration: 2000,
        message: t('toast_notification.subject_editting_error'),
        variant: 'error',
      })
    },
    onSuccess: (conversationResponse) => {
      updateConversationSubjectByIdQueryClient(
        conversationResponse,
        debouncedValue,
      )
      setIsEditMode(false)
      triggerToastNotification({
        duration: 2000,
        message: t('toast_notification.subject_editting_sucessful'),
        variant: 'success',
      })
    },
  })

  const {
    mutate: updateConversationPin,
    isPending: isConversationPinUpdating,
  } = useUpdateConversationMutation({
    onError: () => {
      triggerToastNotification({
        duration: 2000,
        message: isPinned
          ? t('toast_notification.conversation_unpinned_error')
          : t('toast_notification.conversation_pinned_error'),
        variant: 'success',
      })
    },
    onSuccess: (conversationResponse) => {
      updateConversationIdPinnedQueryClient(
        conversationResponse,
        debouncedValue,
      )
      triggerToastNotification({
        duration: 2000,
        message: isPinned
          ? t('toast_notification.conversation_unpinned_successful')
          : t('toast_notification.conversation_pinned_successful'),
        variant: 'success',
      })
    },
  })

  const handleEnableEditMode = () => {
    if (!conversation.subject) return

    onTriggerEvent({
      name: 'kai_rename_conversation_clicked',
      properties: {
        conversationId: conversation.id,
      },
    })
    setIsEditMode(true)
  }

  const onDisableEditMode = () => {
    onTriggerEvent({
      name: 'kai_conversation_rename_cancelled',
    })
    setIsEditMode(false)
  }

  const handlePinConversation = () => {
    onTriggerEvent({
      name: isPinned ? 'kai_conversation_unpinned' : 'kai_conversation_pinned',
      properties: {
        conversationId: conversation.id,
      },
    })
    updateConversationPin({
      id: conversation.id,
      pinned: !isPinned,
    })
  }

  const onSubmit = (text: string) => {
    onTriggerEvent({
      name: 'kai_conversation_rename_submitted',
    })
    updateConversationSubject({ id: conversation.id, subject: text })
  }

  const activateInput = useCallback(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  const handleClickDelete = () => onClickDeleteConversation(conversation)

  const handleClickConversation = (e: MouseEvent | KeyboardEvent) => {
    if (('button' in e && e.button === 1) || e.metaKey || e.ctrlKey) {
      window.open(
        `${window.origin}/ai/conversations/${conversation.id}`,
        '_blank',
        'noopener,noreferrer',
      )
      return
    }
    onSelectConversation(conversation.id)
  }

  const pendingConversationIds = useContext(PendingConversationIdsContext)

  const isConversationPending = pendingConversationIds.has(conversation.id)

  const isActive = activeConversationId === conversation.id

  const isUpdating = isConversationSubjectUpdating || isConversationPinUpdating

  return (
    <li
      className={cn(
        'group flex justify-items-center text-sm text-default cursor-pointer px-2 py-1 rounded-md group relative transition',
        {
          'bg-dark': isActive,
          'bg-transparent py-0 px-[2px]': isEditMode,
          'h-7 bg-light animate-pulse px-2': !conversation.subject && !isActive,
          'lg:hover:bg-subdued lg:focus-within:bg-subdued lg:focus:bg-dark': !isActive && !isPopoverOpen,
          'bg-subdued': isPopoverOpen && !isActive,
        },
      )}
    >

      {isEditMode
        ? (
            <Input
              initialText={conversation.subject}
              isConversationSubjectUpdating={isConversationSubjectUpdating}
              onCancel={onDisableEditMode}
              onComponentMount={activateInput}
              onSubmit={onSubmit}
              ref={inputRef}
            />
          )
        : (
            <>
              {isConversationPending && (
                <div className="mr-2 flex items-center" role="progressbar">
                  <Spinner size="sm" />
                </div>
              )}
              <ConversationSubject
                onClick={handleClickConversation}
                onDoubleClick={handleEnableEditMode}
                subject={conversation.subject || t('new_conversation_subject')}
                expiresAt={conversation.expiresAt}
              />
              <span
                className={cn('hidden group-hover:block group-focus-within:block pointer-events-auto ml-1', {
                  hidden: isEditMode,
                  block: isActive || isMobile() || isPopoverOpen,
                })}
              >
                <ActionButtons
                  isPending={isUpdating}
                  isPinned={isPinned}
                  onCloseCallback={activateInput}
                  onClickDelete={handleClickDelete}
                  onEnableEditMode={handleEnableEditMode}
                  onPinConversation={handlePinConversation}
                  onOpenChange={setIsPopoverOpen}
                />
              </span>
            </>
          )}

    </li>
  )
}
